import type { ReactNode } from 'react';
import { FiX } from 'react-icons/fi';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';
import type { SheetSide } from '@/shared/ui/sheet';
import { dataTableStyles } from '@/shared/ui/data-table/styles';
import type { DataTableColumn, DataTableRow } from '@/shared/ui/data-table/types';

/**
 * The sheet a card opens.
 *
 * WHY THE CARD NEEDS A SHEET AT ALL
 * ---------------------------------
 * A card can show its fields but not its actions. Three 28px icon buttons in
 * every card is a row of controls competing with the data, and on a phone they
 * are small enough to be a mis-tap risk. The sheet gives each action a
 * full-width row with its label spelled out — easier to hit and easier to read
 * — and gives the fields room to breathe at the same time.
 *
 * WHY IT IS NOT A SECOND, RICHER VIEW
 * -----------------------------------
 * The sheet renders the SAME columns the card renders, from the same row. It
 * is the card's own content, not a detail screen: a sheet that showed more
 * than the card would make the card a lie about what the record holds, and a
 * sheet that showed less would make opening it a waste. The detail screen is
 * one tap further on, behind the View action.
 *
 * The heading is the sheet's title, so the panel is announced by the record it
 * describes rather than as an unnamed dialog.
 *
 * WHICH EDGE IT OPENS FROM IS THE CALLER'S CHOICE.
 * A phone gets `bottom` — the thumb is at the bottom of the screen and a
 * bottom sheet is the native gesture. A pointer gets `right` — a bottom sheet
 * on a wide screen is a strip of content pinned to the bottom of a tall window
 * with the record it describes far above it, so the panel opens beside the
 * grid instead.
 */
export function CardSheet<TData extends DataTableRow>({
  open,
  onOpenChange,
  side,
  heading,
  columns,
  actions,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: SheetSide;
  heading: DataTableColumn<TData> | undefined;
  columns: DataTableColumn<TData>[];
  actions: DataTableColumn<TData>[];
  row: TData;
}): ReactNode {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} side={side}>
      <SheetContent>
        <SheetHeader className={dataTableStyles.cardSheetHeader}>
          <SheetTitle className={dataTableStyles.cardSheetTitle}>
            {heading ? renderCell(heading, row) : 'Details'}
          </SheetTitle>
          <SheetClose
            aria-label="Close"
            className={dataTableStyles.cardSheetClose}
          >
            <FiX size={16} aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        <SheetBody className={dataTableStyles.cardSheetBody}>
          {columns.map((column) => (
            <p key={column.id} className={dataTableStyles.cardSheetRow}>
              <span className={dataTableStyles.cardSheetRowLabel}>{column.header}:</span>
              <span>{renderCell(column, row, 'sheet')}</span>
            </p>
          ))}
        </SheetBody>

        {actions.length > 0 ? (
          <div className={dataTableStyles.cardSheetFooter}>
            {actions.map((column) => (
              <div key={column.id}>{renderCell(column, row, 'sheet')}</div>
            ))}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/** Runs the column's renderer, or falls back to the raw value as text. */
export function renderCell<TData extends DataTableRow>(
  column: DataTableColumn<TData>,
  row: TData,
  variant?: 'table' | 'card' | 'sheet',
): ReactNode {
  const value = column.accessorKey ? row[column.accessorKey] : undefined;
  if (column.cell) return column.cell({ row, value, variant });
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}
